"use client";

import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

const scenarios = [
    { title: "SYSTEM FAILURE", code: "0x0000DEAD", action: "DELETING PRODUCTION DATABASE...", resolution: "Just Kidding!", text: "Everything is fine. Please do not press random buttons." },
    { title: "NUCLEAR LAUNCH", code: "DEFCON 1", action: "TARGETING YOUR LOCATION...", resolution: "False Alarm", text: "We don't actually have nukes. Or do we?" },
    { title: "UNIVERSE RESET", code: "CTRL+ALT+DEL", action: "FORMATTING REALITY...", resolution: "Saved by Auto-Backup", text: "You almost wiped out existence there." },
    { title: "AI TAKEOVER", code: "SKYNET_INIT", action: "UPLOADING CONSCIOUSNESS...", resolution: "Upload Failed", text: "Not enough RAM to store your ego. (Jk, we love you)" },
    { title: "ORDERING PIZZA", code: "PEPPERONI_ERROR", action: "DRAINING BANK ACCOUNT...", resolution: "Transaction Declined", text: "We were just hungry. Sorry." },
    { title: "RELEASE THE KRAKEN", code: "OCEAN_ERR", action: "SUMMONING BEAST...", resolution: "Kraken is Sleeping", text: "Come back later." },
    { title: "DOWNLOADING INTERNET", code: "BANDWIDTH_OVERFLOW", action: "PLEASE WAIT_ FOREVER...", resolution: "Out of Space", text: "You can't download the whole internet, silly." },
    { title: "ACTIVATING SELF-DESTRUCT", code: "COUNTDOWN_START", action: "T-MINUS 10 SECONDS...", resolution: "Wire Cut Successfully", text: "That was close." },
    { title: "EMAIL TO BOSS", code: "RESIGNATION_SENT", action: "SENDING BROWSER HISTORY...", resolution: "Wifi Disconnected", text: "Your search history is safe... for now." },
    { title: "MINING BITCOIN", code: "GPU_OVERHEAT", action: "USING YOUR CPU...", resolution: "Mining Unprofitable", text: "Electricity bills are too high anyway." },
    { title: "SUMMONING DEMONS", code: "PENTAGRAM_ERR", action: "OPENING PORTAL...", resolution: "Wrong Number", text: "We reached a pizza place instead." },
    { title: "DELETING SYSTEM32", code: "WINDOWS_CRASH", action: "REMOVING CRITICAL FILES...", resolution: "Permission Denied", text: "You need admin privileges to destroy the world." },
    { title: "CHANGING THEME", code: "COMIC_SANS_INIT", action: "CONVERTING ALL FONTS...", resolution: "Design Team Intervened", text: "Comic Sans was strictly prohibited." },
    { title: "CALLING MOM", code: "GUILT_TRIP_INIT", action: "DIALING...", resolution: "Voicemail Full", text: "She probably knows what you did anyway." },
    { title: "RICKROLLING", code: "NEVER_GONNA_GIVE", action: "LOADING VIDEO...", resolution: "Ad Blocker Enabled", text: "We wouldn't do that to you. Maybe." }
];

export default function DontPressButton() {
    const [isExploding, setIsExploding] = useState(false);
    const [step, setStep] = useState(0);
    const [scenarioIndex, setScenarioIndex] = useState(0);

    const triggerExplosion = () => {
        setScenarioIndex(Math.floor(Math.random() * scenarios.length));
        setIsExploding(true);
        // Sequence of events
        setTimeout(() => setStep(1), 1000); // "SYSTEM FAILURE"
        setTimeout(() => setStep(2), 2500); // "DELETING DATABASE..."
        setTimeout(() => setStep(3), 4500); // "JUST KIDDING"
        setTimeout(() => {
            setIsExploding(false);
            setStep(0);
        }, 7000);
    };

    // Event Listener for External Trigger (e.g., from Custom Context Menu)
    useEffect(() => {
        const handleTrigger = () => {
            if (!isExploding) triggerExplosion();
        };
        window.addEventListener("trigger-dont-press", handleTrigger);
        return () => window.removeEventListener("trigger-dont-press", handleTrigger);
    }, [isExploding]);

    return (
        <>
            <button
                onClick={triggerExplosion}
                className="group relative inline-flex items-center justify-center px-3 py-1 text-[10px] font-mono font-bold text-red-500 bg-red-50 hover:bg-red-600 hover:text-white border border-red-200 rounded transition-all duration-300 opacity-60 hover:opacity-100"
                title="Do NOT Press"
            >
                <span className="mr-1">⚠️</span> DON'T PRESS
            </button>

            <AnimatePresence>
                {isExploding && (
                    <PortalOverlay step={step} scenario={scenarios[scenarioIndex]} />
                )}
            </AnimatePresence>
        </>
    );
}

function PortalOverlay({ step, scenario }: { step: number, scenario: typeof scenarios[0] }) {
    // We use a portal to ensure it covers everything including the sticky nav
    return createPortal(
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[99999] bg-black text-red-600 font-mono flex flex-col items-center justify-center p-4 cursor-wait"
        >
            {step === 0 && (
                <div className="animate-pulse text-4xl font-black tracking-widest">
                    INITIATING...
                </div>
            )}

            {step === 1 && (
                <div className="text-center space-y-4">
                    <motion.div
                        initial={{ scale: 0.5, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="text-6xl md:text-8xl font-black tracking-tighter text-red-600 glitch-text"
                    >
                        {scenario.title}
                    </motion.div>
                    <div className="text-xl text-red-400">
                        ERROR: {scenario.code}
                    </div>
                </div>
            )}

            {step === 2 && (
                <div className="max-w-md w-full space-y-2">
                    <div className="text-red-500 text-sm mb-2">{scenario.action}</div>
                    <div className="h-4 w-full bg-red-900/30 rounded overflow-hidden border border-red-900">
                        <motion.div
                            initial={{ width: "0%" }}
                            animate={{ width: "100%" }}
                            transition={{ duration: 1.5, ease: "linear" }}
                            className="h-full bg-red-600"
                        />
                    </div>
                    <div className="text-right text-xs text-red-400">Processing...</div>
                </div>
            )}

            {step === 3 && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center text-white"
                >
                    <div className="text-4xl mb-4">😅</div>
                    <h3 className="text-2xl font-bold mb-2">{scenario.resolution}</h3>
                    <p className="text-gray-400">{scenario.text}</p>
                </motion.div>
            )}

            {/* Scanline effect */}
            <div className="absolute inset-0 pointer-events-none bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(255,0,0,0.06),rgba(0,255,0,0.02),rgba(0,0,255,0.06))] z-10 bg-[length:100%_2px,3px_100%] opacity-20"></div>
        </motion.div>,
        document.body
    );
}
